import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import {
  LineChart, Line, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis, CartesianGrid,
  BarChart, Bar,
} from "recharts";

const flagOf = (cc) => {
  if (!cc || cc.length !== 2) return "🏳️";
  const A = 0x1F1E6, offset = "A".charCodeAt(0);
  return String.fromCodePoint(A + (cc.toUpperCase().charCodeAt(0) - offset)) +
         String.fromCodePoint(A + (cc.toUpperCase().charCodeAt(1) - offset));
};

export default function SuperAdminOverview() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [signups, setSignups] = useState([]);
  const [logins, setLogins] = useState([]);

  useEffect(() => {
    api.saOverview().then(setData).catch(() => setData({ error: true }));
    api.saTimeseries("signups", "30d").then(setSignups).catch(() => {});
    api.saTimeseries("logins", "30d").then(setLogins).catch(() => {});
  }, []);

  if (!data) return <div className="text-black/50">{t("superadmin.loading")}</div>;
  if (data.error) return <div className="text-[#D90429]">Error loading overview.</div>;

  return (
    <div className="space-y-8" data-testid="sa-overview">
      <header>
        <div className="text-xs font-bold uppercase tracking-widest text-[#F72585]">{t("superadmin.title")}</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{t("superadmin.overview")}</h1>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="sa-kpi-cards">
        <Kpi label={t("superadmin.totalUsers")} value={data.users.total} accent="#4361EE" testid="kpi-total-users" />
        <Kpi label={t("superadmin.new30d")} value={data.users.new_30d} accent="#4CC9F0" testid="kpi-new-30d" />
        <Kpi label={t("superadmin.active30d")} value={data.users.active_30d} accent="#FFB703" testid="kpi-active-30d" />
        <Kpi label={t("superadmin.totalCerts")} value={data.certificates.total_issued} accent="#F72585" testid="kpi-certs" />
      </section>

      <section className="flex flex-wrap gap-2" data-testid="sa-health-row">
        <HealthPill
          ok={!!data.health?.llm_key_configured}
          label={data.health?.llm_key_configured ? "LLM key · configured" : "LLM key · missing"}
          testid="sa-llm-health-pill"
        />
        {data.health?.stripe_configured != null && (
          <HealthPill
            ok={!!data.health?.stripe_configured}
            label={
              data.health?.stripe_configured
                ? `Stripe · ${data.health?.stripe_mode === "live" ? "LIVE" : (data.health?.stripe_mode || "test").toUpperCase()}`
                : "Stripe · missing"
            }
            testid="sa-stripe-health-pill"
          />
        )}
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <Card title={lang === "ar" ? "التسجيلات (٣٠ يوم)" : "Signups (30d)"} testid="chart-signups">
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={signups}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                <YAxis tick={{ fontSize: 10 }} />
                <ReTooltip />
                <Line type="monotone" dataKey="value" stroke="#4361EE" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title={lang === "ar" ? "الدخول (٣٠ يوم)" : "Logins (30d)"} testid="chart-logins">
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={logins}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                <YAxis tick={{ fontSize: 10 }} />
                <ReTooltip />
                <Bar dataKey="value" fill="#F72585" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </section>

      <section className="grid md:grid-cols-3 gap-4">
        <Card title={t("superadmin.byRole")} testid="chart-by-role">
          <ul className="space-y-2">
            {Object.entries(data.users.by_role).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between font-semibold text-sm">
                <span>{k}</span><span className="font-black text-[#4361EE]">{v}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title={t("superadmin.byLanguage")} testid="chart-by-lang">
          <ul className="space-y-2">
            {Object.entries(data.users.by_language).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between font-semibold text-sm">
                <span>{k === "en" ? "English" : "العربية"}</span>
                <span className="font-black text-[#4361EE]">{v}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title={t("superadmin.revenue")} testid="card-revenue">
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-black text-[#4361EE]" data-testid="card-revenue-amount">${(data.revenue || data.revenue_mock).total_amount}</div>
            {data.mode && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black tracking-wider ${
                  data.mode === "live"
                    ? "bg-green-500/15 text-green-700 border border-green-500/40"
                    : "bg-[#FFB703]/25 text-[#7A4B00] border border-[#FFB703]/50"
                }`}
                data-testid="card-revenue-mode-pill"
              >
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${data.mode === "live" ? "bg-green-500 animate-pulse" : "bg-[#FFB703]"}`} />
                {data.mode === "live" ? "LIVE" : "TEST"}
              </span>
            )}
          </div>
          <div className="text-xs text-black/50 mt-1">{(data.revenue || data.revenue_mock).total_orders} {t("superadmin.totalOrders")}</div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <span className="rounded-full bg-[#FFB703]/20 text-[#7A4B00] text-[10px] px-2 py-0.5 font-bold">
              {(data.revenue || data.revenue_mock).by_status.pending} {t("superadmin.pending")}
            </span>
            <span className="rounded-full bg-green-500/15 text-green-700 text-[10px] px-2 py-0.5 font-bold">
              {(data.revenue || data.revenue_mock).by_status.succeeded} {t("superadmin.succeeded")}
            </span>
            <span className="rounded-full bg-black/10 text-black/60 text-[10px] px-2 py-0.5 font-bold">
              {(data.revenue || data.revenue_mock).by_status.cancelled} {t("superadmin.cancelled")}
            </span>
          </div>
        </Card>
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <Card title={t("superadmin.topCourses")} testid="card-top-courses">
          <ul className="space-y-2">
            {data.courses.top_5_by_enrollment.map((c) => (
              <li key={c.course_id} className="flex items-center justify-between text-sm font-semibold">
                <span className="truncate me-2">{c.title?.[lang] || c.title?.en}</span>
                <span className="font-black text-[#F72585]">{c.enrollments}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title={t("superadmin.globalReach")} accent="#F72585" testid="card-global-reach">
          <div className="text-sm text-black/70 font-semibold">
            {t("superadmin.globalReachDesc")
              .replace("{countries}", data.geo.total_unique_countries)
              .replace("{cities}", data.geo.top_5_countries.length ? "· " : "0")
              .replace("{logins}", data.geo.total_logins_30d)}
          </div>
          <ul className="mt-3 space-y-1.5">
            {data.geo.top_5_countries.map((c) => (
              <li key={c.country_code} className="flex items-center justify-between text-sm font-semibold">
                <span>{flagOf(c.country_code)} {c.country_name || c.country_code}</span>
                <span className="font-black text-[#F72585]">{c.count}</span>
              </li>
            ))}
            {data.geo.top_5_countries.length === 0 && (
              <li className="text-xs text-black/40">{t("superadmin.empty")}</li>
            )}
          </ul>
        </Card>
      </section>
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5 shadow-[0_2px_0_rgba(0,0,0,0.04)]" data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-widest text-black/50">{label}</div>
      <div className="text-3xl font-black mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Card({ title, children, accent, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5 shadow-[0_2px_0_rgba(0,0,0,0.04)]" data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: accent || "#4361EE" }}>{title}</div>
      {children}
    </div>
  );
}

function HealthPill({ ok, label, testid }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-black shadow-[0_2px_0_rgba(0,0,0,0.06)] ${
        ok ? "bg-green-500/15 text-green-700 border border-green-500/40"
           : "bg-[#D90429]/15 text-[#D90429] border border-[#D90429]/30"
      }`}
      data-testid={testid}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${ok ? "bg-green-500 animate-pulse" : "bg-[#D90429]"}`} />
      {label}
    </span>
  );
}
